<script setup>
import { storeToRefs } from 'pinia'
import axios from '@axios'
import moment from 'moment-timezone'
import { useVarsStore, useUsersStore, useRestaurantsStore } from '@/stores'
import { useDesignSystem } from '@/composables/useDesignSystem'

const { dialogWidth } = useDesignSystem()
const dWidth = dialogWidth('lg')

const varsStore = useVarsStore()
const { adminDirectReservationDialogVisible, adminEditingReservation } = storeToRefs(varsStore)

const usersStore = useUsersStore()
const { users, myUser, canActForMembers } = storeToRefs(usersStore)

// RM-150: platform admins keep the existing server-side member search.
// Group admins and personal admins instead choose from the pre-loaded list
// of members they're authorized to act for.
const isPlatformAdmin = computed(() => myUser.value?.userLevel === 'admin')
const memberOptions = computed(() => (isPlatformAdmin.value ? users.value : canActForMembers.value))

const restaurantsStore = useRestaurantsStore()
const { restaurantSearchResults } = storeToRefs(restaurantsStore)

// Form state
const form = ref(null)
const saving = ref(false)
const errorMessage = ref('')
const successMessage = ref('')

const selectedMember = ref(null)
const selectedRestaurant = ref(null)
const date = ref('')
const preferredTime = ref('')
const confirmedTime = ref('')
const submittedAt = ref('')
const acceptedAt = ref('')
const confirmedByName = ref('')
const confirmedReservationSystem = ref('')
const confirmedMessageToMember = ref('')
const confirmedMessageToRm = ref('')
const partySize = ref('')
const status = ref('Accepted')
const reservationType = ref('general')
const minSpendPerPerson = ref('')
const actualSpendTotal = ref('')
const market = ref('')
const occasion = ref('General')
const specialRequests = ref('')
const additionalNotes = ref('')

// RM-132 — admin-only opt-out for the 15-min "thank you for accommodating"
// restaurant email. The cron checks this flag at send time, so flipping it
// any time before the schedule fires will suppress the email.
const skipAccommodationThankYouEmail = ref(false)

const isEditMode = computed(() => !!adminEditingReservation.value)
const isOpen = computed({
  get: () => adminDirectReservationDialogVisible.value || isEditMode.value,
  set: val => {
    if (!val) {
      adminDirectReservationDialogVisible.value = false
      adminEditingReservation.value = null
    }
  },
})

const statusOptions = [
  'Draft',
  'Submitted',
  'Awaiting Response from Restaurant',
  'In Review',
  'Member Action',
  'Accepted',
  'Not Available',
  'No Response',
  'Declined',
  'Canceled',
  'Rescinded',
  'Experienced',
]

const reservationTypeOptions = [
  { title: 'General', value: 'general' },
  { title: 'Private', value: 'private' },
]

// Helper: format a UTC date to datetime-local string in the given timezone
const toDatetimeLocal = (utcDate, tz) => {
  if (!utcDate) return ''
  
  return moment.tz(utcDate, tz || 'America/Chicago').format('YYYY-MM-DDTHH:mm')
}

// Pre-populate fields when entering edit mode
watch(adminEditingReservation, res => {
  if (!res) return
  const tz = res.timeZoneIdentifier || 'America/Chicago'

  // Member: use populated owner object if available, fallback to submittedBy stub
  selectedMember.value = res.owner
    ? res.owner
    : (res.submittedBy?.email ? { email: res.submittedBy.email, firstName: '', lastName: '' } : null)

  // Restaurant: build a lightweight object for display; autocomplete uses return-object so it binds directly
  const rName = res.confirmedRestaurant || res.preferredRestaurant || ''
  const rId = res.confirmedRestaurantId || res.preferredRestaurantId || null
  selectedRestaurant.value = rName
    ? { _id: rId, name: rName, market: res.market, timeZoneIdentifier: tz }
    : null

  date.value = res.date || ''
  preferredTime.value = res.preferredTime || ''
  confirmedTime.value = res.confirmedTime || ''
  partySize.value = res.partySize != null ? String(res.partySize) : ''
  status.value = res.status || 'Accepted'
  reservationType.value = res.reservationType || 'general'
  market.value = res.market || ''
  occasion.value = res.occasion || 'General'
  minSpendPerPerson.value = res.minSpendPerPerson != null ? String(res.minSpendPerPerson) : ''
  actualSpendTotal.value = res.actualSpendTotal != null ? String(res.actualSpendTotal) : ''
  confirmedByName.value = res.confirmedByName || ''
  confirmedReservationSystem.value = res.confirmedReservationSystem || ''
  confirmedMessageToMember.value = res.confirmedMessageToMember || ''
  confirmedMessageToRm.value = res.confirmedMessageToRm || ''
  specialRequests.value = res.specialRequests || ''
  additionalNotes.value = res.additionalNotes || ''
  skipAccommodationThankYouEmail.value = Boolean(res.skipAccommodationThankYouEmail)
  submittedAt.value = toDatetimeLocal(res.submittedAt, tz)
  acceptedAt.value = toDatetimeLocal(res.acceptedAt, tz)
  errorMessage.value = ''
  successMessage.value = ''
})

// Auto-fill market when restaurant is selected (create mode only)
watch(selectedRestaurant, restaurant => {
  if (restaurant?.market && !isEditMode.value) {
    market.value = restaurant.market
  }
})

// Auto-fill confirmedTime from preferredTime for Accepted/Experienced (create mode only)
watch(preferredTime, val => {
  if (!isEditMode.value && (status.value === 'Accepted' || status.value === 'Experienced') && !confirmedTime.value) {
    confirmedTime.value = val
  }
})

// Debounced member search (platform admin only — group/personal admins use
// the pre-loaded canActForMembers list, which Vuetify filters client-side).
let memberSearchTimer = null
const onMemberSearch = searchTerm => {
  if (!isPlatformAdmin.value) return
  if (!searchTerm || searchTerm.length < 2) return
  clearTimeout(memberSearchTimer)
  memberSearchTimer = setTimeout(() => {
    usersStore.getSearchedUsers(searchTerm)
  }, 300)
}

// Debounced restaurant search
let restaurantSearchTimer = null
const onRestaurantSearch = searchTerm => {
  if (!searchTerm || searchTerm.length < 2) return
  clearTimeout(restaurantSearchTimer)
  restaurantSearchTimer = setTimeout(() => {
    restaurantsStore.searchRestaurants(searchTerm)
  }, 300)
}

const userDisplayText = user => {
  if (!user) return ''
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim()
  
  return name ? `${name} (${user.email})` : (user.email || '')
}

const restaurantDisplayText = restaurant => {
  if (!restaurant) return ''
  
  return `${restaurant.name} — ${restaurant.market}`
}

const tzHint = computed(() => {
  const tz = selectedRestaurant.value?.timeZoneIdentifier
  
  return tz ? `Restaurant timezone: ${tz}` : null
})

const resetForm = () => {
  selectedMember.value = null
  selectedRestaurant.value = null
  date.value = ''
  preferredTime.value = ''
  confirmedTime.value = ''
  submittedAt.value = ''
  acceptedAt.value = ''
  confirmedByName.value = ''
  confirmedReservationSystem.value = ''
  confirmedMessageToMember.value = ''
  confirmedMessageToRm.value = ''
  partySize.value = ''
  status.value = 'Accepted'
  reservationType.value = 'general'
  minSpendPerPerson.value = ''
  actualSpendTotal.value = ''
  market.value = ''
  occasion.value = 'General'
  specialRequests.value = ''
  additionalNotes.value = ''
  skipAccommodationThankYouEmail.value = false
  errorMessage.value = ''
  successMessage.value = ''
}

const onSubmit = async () => {
  const { valid: isValid } = await form.value.validate()
  if (!isValid) return

  saving.value = true
  errorMessage.value = ''
  successMessage.value = ''

  try {
    const payload = {
      memberEmail: selectedMember.value?.email,
      restaurantId: selectedRestaurant.value?._id,
      restaurantName: selectedRestaurant.value?.name,
      market: market.value,
      date: date.value,
      preferredTime: preferredTime.value,
      confirmedTime: confirmedTime.value || preferredTime.value,
      partySize: partySize.value,
      status: status.value,
      reservationType: reservationType.value,
      occasion: occasion.value || 'General',
    }

    if (minSpendPerPerson.value !== '') payload.minSpendPerPerson = minSpendPerPerson.value
    if (actualSpendTotal.value !== '') payload.actualSpendTotal = actualSpendTotal.value
    if (submittedAt.value) payload.submittedAt = submittedAt.value
    if (acceptedAt.value) payload.acceptedAt = acceptedAt.value
    if (confirmedByName.value) payload.confirmedByName = confirmedByName.value
    if (confirmedReservationSystem.value) payload.confirmedReservationSystem = confirmedReservationSystem.value
    if (confirmedMessageToMember.value) payload.confirmedMessageToMember = confirmedMessageToMember.value
    if (confirmedMessageToRm.value) payload.confirmedMessageToRm = confirmedMessageToRm.value
    if (specialRequests.value) payload.specialRequests = specialRequests.value
    if (additionalNotes.value) payload.additionalNotes = additionalNotes.value

    // RM-132 — always send the flag in edit mode so admins can un-check it
    // after a previous save. In create mode the default (false) is correct.
    if (isEditMode.value) payload.skipAccommodationThankYouEmail = skipAccommodationThankYouEmail.value

    if (isEditMode.value) {
      await axios.put(`/reservation/admin-record/${adminEditingReservation.value._id}`, payload)
      successMessage.value = 'Reservation record updated successfully.'
    } else {
      await axios.post('/reservation/admin-record', payload)
      successMessage.value = 'Reservation record added successfully.'
    }

    setTimeout(() => {
      isOpen.value = false
      resetForm()
    }, 1200)
  } catch (err) {
    errorMessage.value = err?.response?.data?.error || 'An unexpected error occurred.'
  } finally {
    saving.value = false
  }
}

const onCancel = () => {
  isOpen.value = false
  resetForm()
}
</script>

<template>
  <VDialog
    v-if="isOpen"
    v-model="isOpen"
    :width="dWidth"
    persistent
  >
    <VCard class="pa-sm-6 pa-4">
      <VCardItem class="text-center pb-2">
        <VCardTitle class="text-h5">
          {{ isEditMode ? 'Edit Reservation Record' : 'Add Reservation Record' }}
        </VCardTitle>
        <VCardSubtitle>
          {{ isEditMode ? 'Update reservation fields. No emails will be sent.' : 'Directly record a reservation on behalf of a member. No emails will be sent.' }}
        </VCardSubtitle>
      </VCardItem>

      <VCardText>
        <VForm
          ref="form"
          @submit.prevent="onSubmit"
        >
          <VRow>
            <!-- Member -->
            <VCol
              cols="12"
              md="6"
            >
              <VAutocomplete
                v-model="selectedMember"
                :items="memberOptions"
                :item-title="userDisplayText"
                item-value="_id"
                return-object
                :label="isEditMode ? 'Member' : 'Member *'"
                :rules="isEditMode ? [] : [v => !!v || 'Member is required']"
                clearable
                @update:search="onMemberSearch"
              />
            </VCol>

            <!-- Restaurant -->
            <VCol
              cols="12"
              md="6"
            >
              <VAutocomplete
                v-model="selectedRestaurant"
                :items="restaurantSearchResults"
                :item-title="restaurantDisplayText"
                item-value="_id"
                return-object
                :label="isEditMode ? 'Restaurant' : 'Restaurant *'"
                :rules="isEditMode ? [] : [v => !!v || 'Restaurant is required']"
                clearable
                @update:search="onRestaurantSearch"
              />
            </VCol>

            <!-- Date -->
            <VCol
              cols="12"
              md="4"
            >
              <VTextField
                v-model="date"
                label="Date *"
                type="date"
                :rules="[v => !!v || 'Date is required']"
              />
            </VCol>

            <!-- Preferred Time -->
            <VCol
              cols="12"
              md="4"
            >
              <VTextField
                v-model="preferredTime"
                label="Preferred Time *"
                placeholder="7:00pm"
                :rules="[v => !!v || 'Preferred time is required']"
              />
            </VCol>

            <!-- Party Size -->
            <VCol
              cols="12"
              md="4"
            >
              <VTextField
                v-model="partySize"
                label="Party Size *"
                type="number"
                min="1"
                :rules="[v => !!v || 'Party size is required', v => parseInt(v) > 0 || 'Must be at least 1']"
              />
            </VCol>

            <!-- Status -->
            <VCol
              cols="12"
              md="6"
            >
              <VSelect
                v-model="status"
                :items="statusOptions"
                label="Status *"
                :rules="[v => !!v || 'Status is required']"
              />
            </VCol>

            <!-- Reservation Type -->
            <VCol
              cols="12"
              md="6"
            >
              <VSelect
                v-model="reservationType"
                :items="reservationTypeOptions"
                item-title="title"
                item-value="value"
                label="Reservation Type"
              />
            </VCol>

            <!-- Submitted At -->
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="submittedAt"
                label="Submitted At"
                type="datetime-local"
                :hint="tzHint || (isEditMode ? 'Leave blank to keep existing value' : 'Defaults to now if blank')"
                persistent-hint
              />
            </VCol>

            <!-- Accepted At -->
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="acceptedAt"
                label="Accepted At"
                type="datetime-local"
                :hint="tzHint || (isEditMode ? 'Leave blank to keep existing value' : 'Accepted/Experienced only — defaults to now if blank')"
                persistent-hint
              />
            </VCol>

            <!-- Market -->
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="market"
                label="Market *"
                placeholder="Chicago, IL"
                :rules="[v => !!v || 'Market is required']"
              />
            </VCol>

            <!-- Occasion -->
            <VCol
              cols="12"
              md="6"
            >
              <VSelect
                v-model="occasion"
                label="Occasion"
                :items="['Anniversary', 'Birthday', 'Business', 'Date', 'General', 'Special Occasion']"
              />
            </VCol>

            <!-- Confirmed Time -->
            <VCol
              cols="12"
              md="4"
            >
              <VTextField
                v-model="confirmedTime"
                label="Confirmed Time"
                placeholder="7:00pm"
                :hint="isEditMode ? 'Leave blank to keep existing value' : 'Defaults to Preferred Time'"
                persistent-hint
              />
            </VCol>

            <!-- Confirmed By Name -->
            <VCol
              cols="12"
              md="4"
            >
              <VTextField
                v-model="confirmedByName"
                label="Confirmed By Name"
                placeholder="John Smith"
              />
            </VCol>

            <!-- Confirmed Reservation System -->
            <VCol
              cols="12"
              md="4"
            >
              <VTextField
                v-model="confirmedReservationSystem"
                label="Reservation System"
                placeholder="OpenTable, Resy, etc."
              />
            </VCol>

            <!-- Confirmed Message to Member -->
            <VCol
              cols="12"
              md="6"
            >
              <VTextarea
                v-model="confirmedMessageToMember"
                label="Confirmed Message to Member"
                rows="2"
                auto-grow
              />
            </VCol>

            <!-- Confirmed Message to RM -->
            <VCol
              cols="12"
              md="6"
            >
              <VTextarea
                v-model="confirmedMessageToRm"
                label="Confirmed Message to RM"
                rows="2"
                auto-grow
              />
            </VCol>

            <!-- Min Spend / Person -->
            <VCol
              cols="12"
              md="4"
            >
              <VTextField
                v-model="minSpendPerPerson"
                label="Min Spend / Person"
                type="number"
                min="0"
                prefix="$"
              />
            </VCol>

            <!-- Actual Spend Total -->
            <VCol
              cols="12"
              md="4"
            >
              <VTextField
                v-model="actualSpendTotal"
                label="Actual Spend Total"
                type="number"
                min="0"
                prefix="$"
              />
            </VCol>

            <!-- Notes / Requests -->
            <VCol
              cols="12"
              md="6"
            >
              <VTextarea
                v-model="specialRequests"
                label="Notes / Requests"
                rows="2"
                auto-grow
              />
            </VCol>

            <!-- Additional Notes -->
            <VCol
              cols="12"
              md="6"
            >
              <VTextarea
                v-model="additionalNotes"
                label="Additional Notes"
                rows="2"
                auto-grow
              />
            </VCol>

            <!--
              RM-132 — Skip the 15-min restaurant "thank you for
              accommodating" email. Useful when the booking was placed
              outside RM (e.g. directly on OpenTable) so the restaurant
              doesn't get an unsolicited thank-you. 
            -->
            <VCol
              v-if="isEditMode"
              cols="12"
            >
              <VCheckbox
                v-model="skipAccommodationThankYouEmail"
                label="Skip restaurant 'thank you for accommodating' email"
                hint="Suppresses the 15-min post-accept thank-you to the restaurant. Use when the booking was placed outside RM."
                persistent-hint
              />
            </VCol>

            <!-- Error / Success -->
            <VCol
              v-if="errorMessage"
              cols="12"
            >
              <VAlert
                type="error"
                variant="tonal"
                density="compact"
              >
                {{ errorMessage }}
              </VAlert>
            </VCol>
            <VCol
              v-if="successMessage"
              cols="12"
            >
              <VAlert
                type="success"
                variant="tonal"
                density="compact"
              >
                {{ successMessage }}
              </VAlert>
            </VCol>

            <!-- Actions -->
            <VCol
              cols="12"
              class="d-flex flex-wrap justify-center gap-4"
            >
              <VBtn
                type="submit"
                :loading="saving"
              >
                {{ isEditMode ? 'Save Changes' : 'Save Record' }}
              </VBtn>
              <VBtn
                color="secondary"
                variant="tonal"
                :disabled="saving"
                @click="onCancel"
              >
                Cancel
              </VBtn>
            </VCol>
          </VRow>
        </VForm>
      </VCardText>
    </VCard>
  </VDialog>
</template>
